import { COLS, ROWS } from "../../game/constants/board.js";

const CENTER = { x: (COLS - 1) / 2, y: (ROWS - 1) / 2 };

const DEFAULT_DIFFICULTY_BANDS = [
	{ id: "open", difficulty: 1.7, style: "swarm", name: "Serpentine Prime", color: "#55d9ff" },
	{ id: "transit", difficulty: 2.1, style: "transit", name: "Switchback Nexus", color: "#ff6b58" },
	{ id: "folded", difficulty: 2.4, style: "folded", name: "Spiral Relay", color: "#e84dff" },
	{ id: "maze", difficulty: 3.2, style: "maze", name: "Labyrinth Echo", color: "#7cf7d4" },
	{ id: "fortress", difficulty: 3.7, style: "fortress", name: "Fortress Null", color: "#f8e45c" },
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const mulberry32 = (seed) => {
	let value = seed >>> 0;
	return () => {
		value += 0x6D2B79F5;
		let next = value;
		next = Math.imul(next ^ (next >>> 15), next | 1);
		next ^= next + Math.imul(next ^ (next >>> 7), next | 61);
		return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
	};
};

const hashSeed = (...values) => {
	let hash = 2166136261;
	for (const value of values) {
		const text = String(value);
		for (let index = 0; index < text.length; index += 1) {
			hash ^= text.charCodeAt(index);
			hash = Math.imul(hash, 16777619);
		}
	}
	return hash >>> 0;
};

const manhattan = (a, b) => Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]);

const directionKey = (from, to) => {
	if (from[0] === to[0]) return from[1] < to[1] ? "S" : "N";
	return from[0] < to[0] ? "E" : "W";
};

const appendLine = (path, target) => {
	let [x, y] = path[path.length - 1];
	while (x !== target[0] || y !== target[1]) {
		if (x !== target[0]) x += Math.sign(target[0] - x);
		else if (y !== target[1]) y += Math.sign(target[1] - y);
		const last = path[path.length - 1];
		if (!last || last[0] !== x || last[1] !== y) path.push([x, y]);
	}
};

const insideBoard = ([x, y]) => x >= 0 && x < COLS && y >= 0 && y < ROWS;

const countTurns = (path) => {
	let turns = 0;
	let previous = null;
	for (let index = 1; index < path.length; index += 1) {
		const direction = directionKey(path[index - 1], path[index]);
		if (previous && previous !== direction) turns += 1;
		previous = direction;
	}
	return turns;
};

const normalize = (value, min, max) => {
	if (max === min) return 0;
	return clamp((value - min) / (max - min), 0, 1);
};

const titleCase = (value = "") => value ? `${value[0].toUpperCase()}${value.slice(1)}` : value;

const createAdjacencyMap = (path) => {
	const indexMap = new Map();
	path.forEach(([x, y], index) => {
		indexMap.set(`${x},${y}`, index);
	});
	return indexMap;
};

const countFoldContacts = (path, indexMap) => {
	let contacts = 0;
	for (let index = 0; index < path.length; index += 1) {
		const [x, y] = path[index];
		for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
			const otherIndex = indexMap.get(`${x + dx},${y + dy}`);
			if (otherIndex == null || Math.abs(otherIndex - index) <= 2) continue;
			contacts += 1;
		}
	}
	return contacts / 2;
};

const getCoverageScore = (path) => {
	const pathSet = new Set(path.map(([x, y]) => `${x},${y}`));
	let coverable = 0;
	for (let row = 0; row < ROWS; row += 1) {
		for (let col = 0; col < COLS; col += 1) {
			if (pathSet.has(`${col},${row}`)) continue;
			const nearPath = path.some(([x, y]) => Math.abs(x - col) + Math.abs(y - row) <= 2);
			if (nearPath) coverable += 1;
		}
	}
	return coverable;
};

export const analyzeMapDifficulty = (mapDef) => {
	const onBoardPath = mapDef.path.filter(insideBoard);
	const uniqueCells = [...new Set(onBoardPath.map(([x, y]) => `${x},${y}`))].length;
	const turnCount = countTurns(onBoardPath);
	const indexMap = createAdjacencyMap(onBoardPath);
	const foldContacts = countFoldContacts(onBoardPath, indexMap);
	const edgeCells = onBoardPath.filter(([x, y]) => x === 0 || x === COLS - 1 || y === 0 || y === ROWS - 1).length;
	const centerPull = onBoardPath.reduce((sum, [x, y]) => sum + manhattan([x, y], [CENTER.x, CENTER.y]), 0) / Math.max(1, onBoardPath.length);
	const coverage = getCoverageScore(onBoardPath);
	const averageSegment = uniqueCells / Math.max(1, turnCount + 1);

	const pathLengthScore = 1 - normalize(uniqueCells, 52, 150);
	const turnScore = 1 - normalize(turnCount, 6, 40);
	const foldScore = 1 - normalize(foldContacts, 0, 120);
	const edgeScore = normalize(edgeCells / Math.max(1, uniqueCells), 0.15, 0.72);
	const centerScore = normalize(centerPull, 2.4, 8.6);
	const coverageScore = 1 - normalize(coverage, 56, 150);
	const straightScore = normalize(averageSegment, 1.8, 6.8);

	const difficulty = clamp(
		1
		+ pathLengthScore * 1.0
		+ turnScore * 0.8
		+ foldScore * 0.75
		+ edgeScore * 0.8
		+ centerScore * 0.65
		+ coverageScore * 0.7
		+ straightScore * 0.55,
		1,
		5,
	);

	return {
		pathLength: uniqueCells,
		turnCount,
		foldContacts,
		edgeRatio: edgeCells / Math.max(1, uniqueCells),
		centerPull,
		coverage,
		averageSegment,
		difficulty: Number(difficulty.toFixed(2)),
	};
};

export const summarizeDifficultyFactors = (metrics) => {
	const factors = [];
	if (metrics.pathLength <= 72) factors.push("short lane");
	else if (metrics.pathLength >= 112) factors.push("long lane");

	if (metrics.turnCount <= 10) factors.push("few turns");
	else if (metrics.turnCount >= 24) factors.push("high turn density");

	if (metrics.foldContacts >= 28) factors.push("stacked overlap");
	else if (metrics.foldContacts <= 8) factors.push("low overlap");

	if (metrics.edgeRatio >= 0.5) factors.push("edge-heavy routing");
	else if (metrics.edgeRatio <= 0.22) factors.push("center pressure");

	if (metrics.coverage <= 88) factors.push("thin build coverage");
	else if (metrics.coverage >= 120) factors.push("wide build coverage");

	if (metrics.averageSegment >= 5.2) factors.push("long straights");
	else if (metrics.averageSegment <= 2.6) factors.push("tight corners");

	return factors;
};

const buildTransitPath = (target, random) => {
	const path = [[-1, clamp(Math.round(random() * 1), 0, 1)]];
	const passes = clamp(5 + Math.round((4.6 - target) * 1.1), 5, 7);
	const startRow = path[0][1];
	for (let pass = 0; pass < passes; pass += 1) {
		const row = clamp(startRow + pass * 2, 0, ROWS - 2);
		const endCol = pass % 2 === 0 ? COLS - 1 : 1;
		appendLine(path, [path[path.length - 1][0] < 0 ? 0 : path[path.length - 1][0], row]);
		appendLine(path, [endCol, row]);
		if (pass < passes - 1) appendLine(path, [endCol, clamp(row + 1 + (pass % 2), 0, ROWS - 1)]);
	}
	appendLine(path, [path[path.length - 1][0], ROWS - 1]);
	path.push([path[path.length - 1][0], ROWS]);
	return path;
};

const buildSwarmPath = (target, random) => {
	const entryRow = clamp(1 + Math.round(random() * 2), 1, 3);
	const path = [[-1, entryRow], [0, entryRow]];
	const leftCore = clamp(3 + Math.round(random() * 2), 3, 5);
	const rightCore = clamp(COLS - 5 + Math.round(random() * 2), COLS - 5, COLS - 3);
	const midA = clamp(4 + Math.round(random() * 2), 4, 6);
	const midB = clamp(ROWS - 5 + Math.round(random() * 1), ROWS - 5, ROWS - 4);
	const lowRow = clamp(ROWS - 2, 9, ROWS - 1);

	appendLine(path, [rightCore, entryRow]);
	appendLine(path, [rightCore, midA]);
	appendLine(path, [leftCore, midA]);
	appendLine(path, [leftCore, midB]);
	appendLine(path, [COLS - 2, midB]);
	appendLine(path, [COLS - 2, lowRow]);
	appendLine(path, [6, lowRow]);
	appendLine(path, [6, ROWS - 1]);
	appendLine(path, [12 + Math.round(random() * 2), ROWS - 1]);
	path.push([path[path.length - 1][0], ROWS]);
	return path;
};

const buildFoldedPath = (target, random) => {
	const outerBottom = ROWS - 2;
	const path = [[-1, 0], [0, 0]];
	const rightOuter = COLS - 1;
	const innerLeft = clamp(2 + Math.round(random() * 1), 2, 3);
	const innerRight = COLS - 3;
	const coreLeft = clamp(4 + Math.round(random() * 1), 4, 5);
	const coreRight = COLS - 6;

	appendLine(path, [rightOuter, 0]);
	appendLine(path, [rightOuter, outerBottom]);
	appendLine(path, [innerLeft, outerBottom]);
	appendLine(path, [innerLeft, 2]);
	appendLine(path, [innerRight, 2]);
	appendLine(path, [innerRight, outerBottom - 2]);
	appendLine(path, [coreLeft, outerBottom - 2]);
	appendLine(path, [coreLeft, 4]);
	appendLine(path, [coreRight, 4]);
	appendLine(path, [coreRight, ROWS - 1]);
	appendLine(path, [11 + Math.round(random() * 2), ROWS - 1]);
	path.push([path[path.length - 1][0], ROWS]);
	return path;
};

const buildMazePath = (target, random) => {
	const path = [[-1, 0], [0, 0]];
	const rows = [0, 2, 4, 6, 8, 10];
	const anchors = [
		COLS - 4,
		3,
		COLS - 6,
		5,
		COLS - 3,
		7,
	];

	for (let index = 0; index < rows.length; index += 1) {
		appendLine(path, [anchors[index], rows[index]]);
		if (index < rows.length - 1) appendLine(path, [anchors[index], rows[index + 1]]);
	}

	appendLine(path, [COLS - 5, ROWS - 1]);
	appendLine(path, [COLS - 2, ROWS - 1]);
	path.push([path[path.length - 1][0], ROWS]);
	return path;
};

const buildFortressPath = (target, random) => {
	const path = [[-1, 0], [0, 0]];
	const rightOuter = COLS - 1;
	const bottomOuter = ROWS - 1;
	const rightInner = COLS - 4;
	const leftInner = 3;
	const keepRight = COLS - 7;
	const keepLeft = 5;

	appendLine(path, [rightOuter, 0]);
	appendLine(path, [rightOuter, bottomOuter]);
	appendLine(path, [0, bottomOuter]);
	appendLine(path, [0, 3]);
	appendLine(path, [rightInner, 3]);
	appendLine(path, [rightInner, 8]);
	appendLine(path, [leftInner, 8]);
	appendLine(path, [leftInner, 5]);
	appendLine(path, [keepRight, 5]);
	appendLine(path, [keepRight, ROWS - 1]);
	appendLine(path, [keepLeft + Math.round(random() * 2), ROWS - 1]);
	path.push([path[path.length - 1][0], ROWS]);
	return path;
};

const buildPatternPath = ({ style, target, random }) => {
	if (style === "transit") return buildTransitPath(target, random);
	if (style === "folded") return buildFoldedPath(target, random);
	if (style === "maze") return buildMazePath(target, random);
	if (style === "fortress") return buildFortressPath(target, random);
	return buildSwarmPath(target, random);
};

const getThreatLine = (style, metrics) => {
	const notes = summarizeDifficultyFactors(metrics);
	const first = notes[0] || "mixed pressure";
	const second = notes[1] || "reactive routing";
	return `${titleCase(style)} | ${first}, ${second}`;
};

const getMapDescription = (style, metrics) => {
	const traits = summarizeDifficultyFactors(metrics);
	const lead = {
		swarm: "Open lane with long firing windows.",
		transit: "Extended transit corridor with staggered turns.",
		folded: "Collapsed route that doubles back into itself.",
		maze: "Tight maze with repeated coverage checks.",
		fortress: "Layered shell that drags enemies across the perimeter.",
	}[style] || "Procedural combat corridor.";
	return traits.length ? `${lead} ${traits.slice(0, 3).join(", ")}.` : lead;
};

export const generateProceduralMap = ({
	seed = "default",
	targetDifficulty = 2.5,
	style = "swarm",
	name = "Generated Sector",
	color = "#55d9ff",
	maxAttempts = 280,
	difficultyTolerance = 0.3,
} = {}) => {
	const random = mulberry32(hashSeed(seed, targetDifficulty, style));
	let best = null;

	for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
		const candidateRandom = mulberry32(hashSeed(seed, style, targetDifficulty, attempt));
		const path = buildPatternPath({
			target: targetDifficulty,
			style,
			random: candidateRandom,
		});
		if (!path) continue;

		const metrics = analyzeMapDifficulty({ path });
		const error = Math.abs(metrics.difficulty - targetDifficulty);
		const candidate = {
			name,
			desc: getMapDescription(style, metrics),
			color,
			threat: getThreatLine(style, metrics),
			path,
			metrics,
			seed: `${seed}:${attempt}`,
			generated: true,
			targetDifficulty,
			style,
		};

		if (!best || error < best.error) best = { error, map: candidate };
		if (error <= difficultyTolerance) return candidate;
	}

	return best?.map || {
		name,
		desc: "Procedural sector fallback.",
		color,
		threat: `Difficulty ${targetDifficulty.toFixed(1)}`,
		path: [[-1, 1], [0, 1], [1, 1], [2, 1], [3, 1], [3, 2], [3, 3], [4, 3], [5, 3], [6, 3], [7, 3], [8, 3], [8, 4], [8, 5], [8, 6], [9, 6], [10, 6], [11, 6], [12, 6], [13, 6], [13, 7], [13, 8], [13, 9], [13, 10], [13, 11], [13, 12]],
		generated: true,
		targetDifficulty,
		style,
	};
};

export const generateMapPack = ({
	seed = "stellar-pack-v1",
	difficulties = DEFAULT_DIFFICULTY_BANDS,
} = {}) => difficulties.map((band, index) => generateProceduralMap({
	seed: `${seed}:${band.id}:${index}`,
	targetDifficulty: band.difficulty,
	style: band.style,
	name: band.name,
	color: band.color,
}));

export const createDailyMapPack = (dayKey, count = 3) => {
	const difficulties = DEFAULT_DIFFICULTY_BANDS
		.slice(0, count)
		.map((band, index) => ({
			...band,
			name: `Daily ${index + 1}`,
			difficulty: clamp(band.difficulty + index * 0.18, 1.5, 4.6),
		}));
	return generateMapPack({ seed: `daily:${dayKey}`, difficulties });
};

export const createWeeklyMapPack = (weekKey, count = 5) => {
	const difficulties = DEFAULT_DIFFICULTY_BANDS
		.slice(0, count)
		.map((band, index) => ({
			...band,
			name: `Weekly ${index + 1}`,
			difficulty: clamp(band.difficulty + (index % 2 === 0 ? 0.12 : -0.06), 1.5, 4.8),
		}));
	return generateMapPack({ seed: `weekly:${weekKey}`, difficulties });
};

export const DEFAULT_MAP_DIFFICULTIES = DEFAULT_DIFFICULTY_BANDS;
